import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import SwapWrestler from '../../components/wrestler/SwapWrestler';
import ProtectedRoute from '../../components/ProtectedRoute';
import Modal from '../../components/ui/Modal';
import {
  buildFactionLineupHistoryRepair,
  getEmptyLineupSnapshotCount
} from '../../utils/factionLineupRepair';
import { buildFactionLineupHistoryRows } from '../../utils/factionLineupHistory';
import { buildChangeDescription, recordAdminUpdate } from '../../utils/adminUpdates';


// ✅ This is ONLY a functional helper, NOT a component
function useInitialFactionProps({ initialOwnerId, initialRosterName, setForm }) {
  useEffect(() => {
    if (initialOwnerId) {
      setForm(prev => ({ ...prev, ownerUid: initialOwnerId }));
    }
    if (initialRosterName) {
      setForm(prev => ({ ...prev, roster: initialRosterName }));
    }
  }, [initialOwnerId, initialRosterName, setForm]);
}

// ✅ Now the correct export default:
function normalizeDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value === 'number') {
    const parsedFromNumber = new Date(value);
    return Number.isNaN(parsedFromNumber.getTime()) ? null : parsedFromNumber;
  }
  if (typeof value === 'string') {
    const parsedFromString = Date.parse(value);
    return Number.isNaN(parsedFromString) ? null : new Date(parsedFromString);
  }
  return null;
}

function getFactionRosterLabel(faction, rosterMap) {
  const rawRoster = faction?.roster;
  if (typeof rawRoster === 'string' && rawRoster.trim()) return rawRoster.trim();
  if (rawRoster && typeof rawRoster === 'object' && rawRoster.name) return rawRoster.name;
  if (faction?.rosterId && rosterMap.has(faction.rosterId)) {
    return rosterMap.get(faction.rosterId)?.name || faction.rosterId;
  }
  return faction?.rosterId || '';
}

function getFactionMembers(faction) {
  if (Array.isArray(faction?.wrestlers) && faction.wrestlers.length > 0) return faction.wrestlers;
  if (Array.isArray(faction?.costs) && faction.costs.length > 0) return faction.costs;
  if (Array.isArray(faction?.wrestlerIds) && faction.wrestlerIds.length > 0) return faction.wrestlerIds;
  return [];
}

function getFactionWrestlerCount(faction) {
  return getFactionMembers(faction).length;
}

function getFactionScore(faction) {
  if (Array.isArray(faction?.wrestlers) && faction.wrestlers.length > 0) {
    return faction.wrestlers.reduce(
      (sum, wrestler) => sum + Number(wrestler.accumulatedPoints ?? wrestler.currentScore ?? 0),
      0
    );
  }

  if (Array.isArray(faction?.costs) && faction.costs.length > 0) {
    return faction.costs.reduce(
      (sum, cost) => sum + Number(cost.currentScore ?? cost.accumulatedPoints ?? 0),
      0
    );
  }

  return 0;
}

function getFactionGameType(faction, rosterMap) {
  if (faction?.gameType) return faction.gameType;
  if (faction?.rosterId && rosterMap.has(faction.rosterId)) {
    return rosterMap.get(faction.rosterId)?.gameType || '';
  }
  return '';
}

function getFactionCostRows(faction) {
  if (Array.isArray(faction?.costs) && faction.costs.length > 0) {
    return faction.costs.map(cost => ({ wrestlerId: cost.wrestlerId, cost: cost.cost }));
  }

  if (Array.isArray(faction?.wrestlers) && faction.wrestlers.length > 0) {
    return faction.wrestlers.map(wrestler => ({
      wrestlerId: wrestler.id || wrestler.wrestlerId || '',
      cost: wrestler.cost || 0,
    }));
  }

  return [{ wrestlerId: '', cost: 0 }];
}

function getWrestlerDisplayName(wrestlerId, wrestlerNameMap) {
  return wrestlerNameMap.get(wrestlerId) || wrestlerId || '-';
}

export default function AddFaction({
  initialOwnerId = '',
  initialRosterName = '',
  maxWrestlers = 6,
  onSuccess,
}) {
  // Stato globale per abilitare/disabilitare scambi (Firestore)
  const [swapsEnabled, setSwapsEnabled] = useState(true);
  const [swapsBlockedAt, setSwapsBlockedAt] = useState(null);
  const [swapsLoading, setSwapsLoading] = useState(true);
  const [swapsError, setSwapsError] = useState('');
  const [swapsRoadToEnabled, setSwapsRoadToEnabled] = useState(true);
  const [swapsRoadToBlockedAt, setSwapsRoadToBlockedAt] = useState(null);
  const [swapsRoadToLoading, setSwapsRoadToLoading] = useState(true);
  const [swapsRoadToError, setSwapsRoadToError] = useState('');

  // Carica stato scambi da Firestore
  useEffect(() => {
    async function fetchSwapsSetting() {
      setSwapsLoading(true);
      setSwapsError('');
      try {
        const docRef = doc(db, 'settings', 'swaps');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          setSwapsEnabled(!!data.enabled);
          setSwapsBlockedAt(data.blockedAt ? (data.blockedAt.toDate ? data.blockedAt.toDate() : new Date(data.blockedAt)) : null);
        }
      } catch (err) {
        setSwapsError('Errore nel caricamento stato scambi');
      } finally {
        setSwapsLoading(false);
      }
    }
    fetchSwapsSetting();
  }, []);

  // Carica stato scambi Road To da Firestore
  useEffect(() => {
    async function fetchSwapsRoadToSetting() {
      setSwapsRoadToLoading(true);
      setSwapsRoadToError('');
      try {
        const docRef = doc(db, 'settings', 'swapsRoadTo');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          setSwapsRoadToEnabled(!!data.enabled);
          setSwapsRoadToBlockedAt(data.blockedAt ? (data.blockedAt.toDate ? data.blockedAt.toDate() : new Date(data.blockedAt)) : null);
        }
      } catch (err) {
        setSwapsRoadToError('Errore nel caricamento stato scambi Road To');
      } finally {
        setSwapsRoadToLoading(false);
      }
    }
    fetchSwapsRoadToSetting();
  }, []);

  // Aggiorna stato scambi su Firestore con conferma
  const handleToggleSwaps = async () => {
    const next = !swapsEnabled;
    if (!next) {
      if (!window.confirm('Sei sicuro di voler bloccare gli scambi per tutte le fazioni?')) return;
    }
    setSwapsLoading(true);
    setSwapsError('');
    try {
      const docRef = doc(db, 'settings', 'swaps');
      // Prova updateDoc, se fallisce perché il doc non esiste, usa setDoc
      try {
        await updateDoc(docRef, {
          enabled: next,
          blockedAt: !next ? serverTimestamp() : null
        });
      } catch (err) {
        // Se il documento non esiste, crealo
        if (err.code === 'not-found' || err.message?.includes('No document to update')) {
          const { setDoc } = await import('firebase/firestore');
          await setDoc(docRef, {
            enabled: next,
            blockedAt: !next ? serverTimestamp() : null
          });
        } else {
          throw err;
        }
      }
      setSwapsEnabled(next);
      setSwapsBlockedAt(!next ? new Date() : null);
    } catch (err) {
      setSwapsError('Errore nel salvataggio stato scambi');
    } finally {
      setSwapsLoading(false);
    }
  };

  // Aggiorna stato scambi Road To su Firestore con conferma
  const handleToggleSwapsRoadTo = async () => {
    const next = !swapsRoadToEnabled;
    if (!next) {
      if (!window.confirm('Sei sicuro di voler bloccare gli scambi per tutte le fazioni Road To?')) return;
    }
    setSwapsRoadToLoading(true);
    setSwapsRoadToError('');
    try {
      const docRef = doc(db, 'settings', 'swapsRoadTo');
      try {
        await updateDoc(docRef, {
          enabled: next,
          blockedAt: !next ? serverTimestamp() : null
        });
      } catch (err) {
        if (err.code === 'not-found' || err.message?.includes('No document to update')) {
          const { setDoc } = await import('firebase/firestore');
          await setDoc(docRef, {
            enabled: next,
            blockedAt: !next ? serverTimestamp() : null
          });
        } else {
          throw err;
        }
      }
      setSwapsRoadToEnabled(next);
      setSwapsRoadToBlockedAt(!next ? new Date() : null);
    } catch (err) {
      setSwapsRoadToError('Errore nel salvataggio stato scambi Road To');
    } finally {
      setSwapsRoadToLoading(false);
    }
  };

  // Banner per scambi disattivati
  const renderSwapBanner = () => (
    <div className="alert alert-info d-flex align-items-center my-4">
      <i className="bi bi-info-circle-fill fs-4 me-3"></i>
      <div>
        <h5 className="mb-1">Scambi temporaneamente disattivati</h5>
        <p className="mb-0">Gli scambi dei wrestler per le fazioni Indipendenti sono attualmente disattivati e verranno riattivati prossimamente.</p>
      </div>
    </div>
  );
  const navigate = useNavigate();

  // Stato aggiuntivo per reset di massa
  const [isResetting, setIsResetting] = useState(false);
  const [isRepairingLineups, setIsRepairingLineups] = useState(false);
  const [repairingFactionId, setRepairingFactionId] = useState('');

  const [factions, setFactions] = useState([]);
  const [wrestlers, setWrestlers] = useState([]);
  const [users, setUsers] = useState([]);
  const [rosters, setRosters] = useState([]);
  const rosterMap = useMemo(
    () => new Map(rosters.filter(Boolean).map(roster => [roster.id, roster])),
    [rosters]
  );
  const [showNewRosterInput, setShowNewRosterInput] = useState(false);
  const [newRosterName, setNewRosterName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [gameTypeFilter, setGameTypeFilter] = useState('all');
  const [rosterFilter, setRosterFilter] = useState('all');
  const emptyLineupRepairStats = useMemo(() => {
    return factions.reduce(
      (stats, faction) => {
        const emptyCount = getEmptyLineupSnapshotCount(faction);
        if (emptyCount > 0) {
          stats.factionCount += 1;
          stats.snapshotCount += emptyCount;
        }
        return stats;
      },
      { factionCount: 0, snapshotCount: 0 }
    );
  }, [factions]);

  // Aggiungi questo stato all'inizio del componente (dopo gli altri useState)
  const [bulkMoveTarget, setBulkMoveTarget] = useState('');
  // Funzione per azzerare i punti di tutte le fazioni di un roster selezionato
  const handleResetAllPointsInRoster = async () => {
    if (rosterFilter === 'all') {
      setMessage('Seleziona un roster specifico');
      return;
    }
    const confirmMessage = `Confermi di voler azzerare i punti per TUTTE le fazioni del roster "${rosterFilter}"?\nI punteggi dei wrestler verranno resettati.`;
    if (!window.confirm(confirmMessage)) return;
    setIsResetting(true);
    setMessage('Reset in corso...');
    try {
      // 1) Filtra le fazioni del roster selezionato
      const factionsToReset = factions.filter(
        f => getFactionRosterLabel(f, rosterMap).toLowerCase() === rosterFilter.toLowerCase()
      );
      // 2) Prepara tutte le operazioni di update
      const batch = [];
      for (const f of factionsToReset) {
        const factionRef = doc(db, 'factions', f.id);
        const factionSnap = await getDoc(factionRef);
        if (!factionSnap.exists()) continue;
        const factionData = factionSnap.data();
        // Aggiorna i costi con i nuovi startScore
        const members = Array.isArray(factionData.wrestlers) ? factionData.wrestlers : (factionData.costs || []);
        const updatedMembers = await Promise.all(
          members.map(async (member) => {
            const wrestlerId = member.id || member.wrestlerId;
            const wrestlerRef = doc(db, 'wrestlers', wrestlerId);
            const wrestlerSnap = await getDoc(wrestlerRef);
            const fallbackScore = member.currentScore ?? member.accumulatedPoints ?? member.startScore ?? 0;
            const trueScore = wrestlerSnap.exists() ? wrestlerSnap.data().score : fallbackScore;
            return {
              ...member,
              startScore: trueScore,
            };
          })
        );
        batch.push(
          updateDoc(
            factionRef,
            Array.isArray(factionData.wrestlers)
              ? { wrestlers: updatedMembers }
              : { costs: updatedMembers }
          )
        );
      }
      // 3) Esegui tutte le operazioni in batch
      await Promise.all(batch);
      await loadData();
      setMessage(`Punti azzerati per ${factionsToReset.length} fazioni del roster ${rosterFilter}!`);
    } catch (err) {
      console.error(err);
      setMessage('Errore durante il reset dei punti');
    } finally {
      setIsResetting(false);
    }
  };

  // Stato per la modale di creazione roster
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [newRosterData, setNewRosterData] = useState({ name: '', description: '', dataInizioPunteggio: '' });

  // Funzioni per gestire ordinamento
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Calcola fazioni filtrate e ordinate
  const filteredAndSortedFactions = useMemo(() => {
    let result = [...factions];

    // Filtra per testo di ricerca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(f =>
        (f.name && f.name.toLowerCase().includes(term)) ||
        getFactionRosterLabel(f, rosterMap).toLowerCase().includes(term)
      );
    }

    // Filtra per tipo di gioco
    if (gameTypeFilter !== 'all') {
      result = result.filter(f => getFactionGameType(f, rosterMap) === gameTypeFilter);
    }

    // Filtra per roster
    if (rosterFilter !== 'all') {
      result = result.filter(
        f => getFactionRosterLabel(f, rosterMap).toLowerCase() === rosterFilter.toLowerCase()
      );
    }

    // Ordina i risultati
    result.sort((a, b) => {
      let aValue = '';
      let bValue = '';

      switch (sortField) {
        case 'score':
          aValue = getFactionScore(a);
          bValue = getFactionScore(b);
          break;

        case 'numWrestler':
          aValue = getFactionWrestlerCount(a);
          bValue = getFactionWrestlerCount(b);
          break;

        case 'type':
          aValue = getFactionGameType(a, rosterMap).toLowerCase();
          bValue = getFactionGameType(b, rosterMap).toLowerCase();
          break;

        case 'date':
          aValue = normalizeDateValue(a.createdAt)?.getTime() || 0;
          bValue = normalizeDateValue(b.createdAt)?.getTime() || 0;
          break;

        case 'roster': {
          aValue = getFactionRosterLabel(a, rosterMap).toLowerCase();
          bValue = getFactionRosterLabel(b, rosterMap).toLowerCase();
          break;
        }

        case 'name':
        default:
          aValue = (a.name ?? '').toString().toLowerCase();
          bValue = (b.name ?? '').toString().toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [factions, gameTypeFilter, rosterFilter, rosterMap, searchTerm, sortDirection, sortField]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    roster: '',
    ownerUid: '',
    gameType: 'Competitivo',
    costs: [{ wrestlerId: '', cost: 0 }]
  });

  useInitialFactionProps({ initialOwnerId, initialRosterName, setForm });

  const initialCredits = 300;
  const [message, setMessage] = useState('');
  const wrestlerNameMap = useMemo(
    () => new Map(
      wrestlers
        .filter(Boolean)
        .map((wrestler) => [
          wrestler.id,
          wrestler.name || wrestler.ringName || wrestler.displayName || wrestler.id
        ])
    ),
    [wrestlers]
  );
  const editingFaction = useMemo(
    () => (editingId ? factions.find((faction) => faction.id === editingId) || null : null),
    [editingId, factions]
  );
  const editingLineupHistoryRows = useMemo(
    () => buildFactionLineupHistoryRows(editingFaction),
    [editingFaction]
  );
  const editingFactionWrestlerNameMap = useMemo(() => {
    const names = new Map(wrestlerNameMap);

    getFactionMembers(editingFaction).forEach((member) => {
      if (!member || typeof member === 'string') return;

      const wrestlerId = member.id || member.wrestlerId;
      const wrestlerName = member.name || member.ringName || member.displayName;
      if (wrestlerId && wrestlerName && !names.has(wrestlerId)) {
        names.set(wrestlerId, wrestlerName);
      }
    });

    return names;
  }, [editingFaction, wrestlerNameMap]);

  const formatRosterStartDate = (date) => {
    if (!date) return '';
    let d;
    if (date.seconds) d = new Date(date.seconds * 1000);
    else if (date.toDate) d = date.toDate();
    else if (date instanceof Date) d = date;
    else {
      const parsed = Date.parse(date);
      if (isNaN(parsed)) return '';
      d = new Date(parsed);
    }
    return d.toLocaleDateString('it-IT');
  };

  // load all data
  async function loadData() {
    // wrestlers
    const wSnap = await getDocs(collection(db, 'wrestlers'));
    const sortedWrestlers = wSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => a.name.localeCompare(b.name));
    setWrestlers(sortedWrestlers);

    // users without faction
    const uSnap = await getDocs(collection(db, 'users'));
    const allUsers = uSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    setUsers(allUsers);

    // rosters
    const rSnap = await getDocs(collection(db, 'rosters'));
    setRosters(rSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    // factions
    const fSnap = await getDocs(collection(db, 'factions'));
    const data = fSnap.docs.map(d => {
      const raw = d.data().createdAt;
      return {
        id: d.id,
        ...d.data(),
        createdAt: raw && raw.toDate ? raw.toDate() : raw
      };
    });
    setFactions(data);
  }

  useEffect(() => { loadData(); }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({ name: '', roster: '', ownerUid: '', gameType: 'Competitivo', costs: [{ wrestlerId: '', cost: 0 }] });
    setMessage('');
  };

  const startEdit = f => {
    setEditingId(f.id);
    setForm({
      name: f.name || '',
      roster: getFactionRosterLabel(f, rosterMap),
      ownerUid: f.ownerUid || f.ownerId || '',
      gameType: getFactionGameType(f, rosterMap) || 'Competitivo',
      costs: getFactionCostRows(f)
    });
    setMessage('');
  };

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const updateCost = (idx, key, value) => {
    const arr = [...form.costs];
    arr[idx][key] = key === 'cost' ? Number(value) : value;
    updateField('costs', arr);
  };
  const addRow = () => updateField('costs', [...form.costs, { wrestlerId: '', cost: 0 }]);
  const removeRow = i => updateField('costs', form.costs.filter((_, idx) => idx !== i));

  const totalCost = form.costs.reduce((sum, m) => sum + (m.cost || 0), 0);
  const creditsLeft = initialCredits - totalCost;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!editingId || !form.name || !form.roster || !form.ownerUid || creditsLeft < 0) return;

    // Prepara i dati dei costi preservando gli score esistenti
    const existingFaction = factions.find(f => f.id === editingId);
    const selectedRoster = rosters.find(roster => roster.name === form.roster);
    const selectedRosterId = selectedRoster?.id || existingFaction?.rosterId || '';
    const usesNewFactionSchema = Array.isArray(existingFaction?.wrestlers) || Boolean(existingFaction?.rosterId);
    let costsData;
    if (editingId) {
      // MODALITÀ AGGIORNAMENTO: preserva gli score esistenti
      const existingWrestlersMap = new Map();
      if (Array.isArray(existingFaction?.wrestlers) && existingFaction.wrestlers.length > 0) {
        existingFaction.wrestlers.forEach(wrestler => {
          existingWrestlersMap.set(wrestler.id || wrestler.wrestlerId, wrestler);
        });
      } else {
        (existingFaction?.costs || []).forEach(cost => {
          existingWrestlersMap.set(cost.wrestlerId, cost);
        });
      }

      costsData = await Promise.all(
        form.costs.map(async m => {
          // Se il wrestler è già presente, mantieni gli score esistenti
          if (existingWrestlersMap.has(m.wrestlerId)) {
            const existing = existingWrestlersMap.get(m.wrestlerId);
            if (usesNewFactionSchema) {
              return {
                ...existing,
                id: existing.id || existing.wrestlerId || m.wrestlerId,
                cost: m.cost,
                startScore: existing.startScore || 0,
                accumulatedPoints: existing.accumulatedPoints ?? existing.currentScore ?? 0,
                status: existing.status || 'In_Ring'
              };
            }
            return {
              wrestlerId: m.wrestlerId,
              cost: m.cost,
              startScore: existing.startScore || 0,
              currentScore: existing.currentScore || 0
            };
          }
          const wSnap = await getDoc(doc(db, 'wrestlers', m.wrestlerId));
          const start = wSnap.exists() ? wSnap.data().score || 0 : 0;
          if (usesNewFactionSchema) {
            return {
              id: m.wrestlerId,
              cost: m.cost,
              startScore: start,
              accumulatedPoints: 0,
              status: 'In_Ring'
            };
          }
          return {
            wrestlerId: m.wrestlerId,
            cost: m.cost,
            startScore: start,
            currentScore: start
          };
        })
      );
    } else {
      // MODALITÀ CREAZIONE: imposta nuovi score
      costsData = await Promise.all(
        form.costs.map(async m => {
          const wSnap = await getDoc(doc(db, 'wrestlers', m.wrestlerId));
          const start = wSnap.exists() ? wSnap.data().score || 0 : 0;
          if (usesNewFactionSchema) {
            return {
              id: m.wrestlerId,
              cost: m.cost,
              startScore: start,
              accumulatedPoints: 0,
              status: 'In_Ring'
            };
          }
          return {
            wrestlerId: m.wrestlerId,
            cost: m.cost,
            startScore: start,
            currentScore: start
          };
        })
      );
    }

    // Assicurati che i valori numerici non siano undefined
    const payload = {
      name: form.name,
      roster: form.roster,
      rosterId: selectedRosterId,
      ownerUid: form.ownerUid,
      gameType: getFactionGameType(
        {
          ...existingFaction,
          gameType: form.gameType,
          rosterId: selectedRosterId
        },
        rosterMap
      ) || form.gameType,
      creditsLeft: creditsLeft || 0,
      totalCost: totalCost || 0,
      initialCredits: initialCredits || 300,
      malus: editingId
        ? factions.find(f => f.id === editingId)?.malus || 0
        : 0,
      updatedAt: serverTimestamp(),
    };

    if (usesNewFactionSchema) {
      payload.wrestlers = costsData;
    } else {
      payload.wrestlerIds = costsData.map(c => c.wrestlerId);
      payload.costs = costsData;
    }

    try {
      await updateDoc(doc(db, 'factions', editingId), payload);
      await recordAdminUpdate({
        type: 'Fazione',
        action: 'Modifica',
        title: payload.name,
        description: buildChangeDescription('Fazione modificata', existingFaction, payload, [
          { key: 'name', label: 'nome' },
          { key: 'roster', label: 'roster', getBefore: faction => getFactionRosterLabel(faction, rosterMap) },
          {
            key: 'ownerUid',
            label: 'proprietario',
            format: value => users.find(user => user.id === value)?.username || users.find(user => user.id === value)?.email || value || '-'
          },
          { key: 'gameType', label: 'tipo gioco' },
          {
            key: 'wrestlers',
            label: 'wrestler',
            getBefore: faction => getFactionMembers(faction).map(member => member.id || member.wrestlerId || member),
            getAfter: faction => (Array.isArray(faction.wrestlers) ? faction.wrestlers : faction.costs || []).map(member => member.id || member.wrestlerId || member),
            format: value => {
              const ids = Array.isArray(value) ? value : [];
              if (ids.length === 0) return 'nessuno';
              return ids.map(id => getWrestlerDisplayName(id, wrestlerNameMap)).join(', ');
            }
          }
        ]),
        entityType: 'factions',
        entityId: editingId
      });

      await loadData();
      resetForm();
      setMessage('Fazione salvata con successo');
      if (onSuccess) onSuccess(); // Chiamata al callback
    } catch (err) {
      console.error(err);
      setMessage('Errore nel salvataggio');
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Confermi eliminazione?')) return;
    try {
      const faction = factions.find(item => item.id === id);
      await deleteDoc(doc(db, 'factions', id));
      await recordAdminUpdate({
        type: 'Fazione',
        action: 'Eliminazione',
        title: faction?.name || id,
        description: `Fazione eliminata: ${faction?.name || id}`,
        entityType: 'factions',
        entityId: id
      });
      await loadData();
      setMessage('Fazione eliminata');
    } catch (err) {
      console.error(err);
      setMessage('Errore eliminazione');
    }
  };


  const handleResetPoints = async (factionId) => {
    if (
      !window.confirm(
        'Confermi di voler azzerare i punti per questa fazione?\nI punteggi dei wrestler verranno resettati.'
      )
    )
      return;

    try {
      // 1) Prendi la fazione
      const factionRef = doc(db, 'factions', factionId);
      const factionSnap = await getDoc(factionRef);
      if (!factionSnap.exists()) {
        setMessage('Fazione non trovata');
        return;
      }
      const factionData = factionSnap.data();

      const members = Array.isArray(factionData.wrestlers) ? factionData.wrestlers : (factionData.costs || []);
      const updatedMembers = await Promise.all(
        members.map(async (member) => {
          const wrestlerId = member.id || member.wrestlerId;
          const wrestlerRef = doc(db, 'wrestlers', wrestlerId);
          const wrestlerSnap = await getDoc(wrestlerRef);

          const trueScore = wrestlerSnap.exists()
            ? wrestlerSnap.data().score
            : (member.currentScore ?? member.accumulatedPoints ?? member.startScore ?? 0);

          return {
            ...member,
            startScore: trueScore,
          };
        })
      );

      // 3) Scrivi su Firestore
      await updateDoc(
        factionRef,
        Array.isArray(factionData.wrestlers)
          ? { wrestlers: updatedMembers }
          : { costs: updatedMembers }
      );

      // 4) Ricarica e notifica
      await loadData();
      setMessage('Punti azzerati con successo!');
    } catch (err) {
      console.error(err);
      setMessage('Errore durante il reset dei punti');
    }
  };

  const repairFactionEmptyLineupSnapshots = async (factionId, { askConfirm = true } = {}) => {
    const factionRef = doc(db, 'factions', factionId);
    const factionSnap = await getDoc(factionRef);

    if (!factionSnap.exists()) {
      return { fixed: false, removedCount: 0, missing: true };
    }

    const factionData = factionSnap.data();
    const repair = buildFactionLineupHistoryRepair(factionData);

    if (!repair.needsRepair) {
      return { fixed: false, removedCount: 0, missing: false };
    }

    if (
      askConfirm &&
      !window.confirm(
        `Correggere la line-up di "${factionData.name || factionId}" rimuovendo ${repair.removedCount} snapshot vuot${repair.removedCount === 1 ? 'a' : 'e'}?`
      )
    ) {
      return { fixed: false, removedCount: 0, missing: false, cancelled: true };
    }

    await updateDoc(factionRef, {
      lineupHistory: repair.cleanedLineupHistory,
      updatedAt: serverTimestamp()
    });

    return {
      fixed: true,
      removedCount: repair.removedCount,
      missing: false
    };
  };

  const handleRepairFactionLineupHistory = async (factionId) => {
    setRepairingFactionId(factionId);
    setMessage('Correzione line-up in corso...');

    try {
      const result = await repairFactionEmptyLineupSnapshots(factionId);

      if (result.cancelled) {
        setMessage('Correzione line-up annullata.');
        return;
      }

      if (result.missing) {
        setMessage('Fazione non trovata.');
        return;
      }

      if (!result.fixed) {
        setMessage('Nessuna snapshot line-up vuota trovata per questa fazione.');
        return;
      }

      await loadData();
      setMessage(`Correzione line-up completata con successo: rimoss${result.removedCount === 1 ? 'a' : 'e'} ${result.removedCount} snapshot vuot${result.removedCount === 1 ? 'a' : 'e'}.`);
    } catch (err) {
      console.error(err);
      setMessage('Errore durante la correzione line-up');
    } finally {
      setRepairingFactionId('');
    }
  };

  const handleRepairAllLineupHistories = async () => {
    setIsRepairingLineups(true);
    setMessage('Verifica line-up in corso...');

    try {
      const factionsSnapshot = await getDocs(collection(db, 'factions'));
      const factionsToRepair = factionsSnapshot.docs
        .map((factionDoc) => ({ id: factionDoc.id, ...factionDoc.data() }))
        .filter((faction) => getEmptyLineupSnapshotCount(faction) > 0);

      if (factionsToRepair.length === 0) {
        await loadData();
        setMessage('Nessuna snapshot line-up vuota trovata.');
        return;
      }

      const totalEmptySnapshots = factionsToRepair.reduce(
        (sum, faction) => sum + getEmptyLineupSnapshotCount(faction),
        0
      );

      if (
        !window.confirm(
          `Correggere ${factionsToRepair.length} fazion${factionsToRepair.length === 1 ? 'e' : 'i'} rimuovendo ${totalEmptySnapshots} snapshot line-up vuot${totalEmptySnapshots === 1 ? 'a' : 'e'}?`
        )
      ) {
        setMessage('Correzione line-up annullata.');
        return;
      }

      setMessage('Correzione line-up in corso...');
      const results = await Promise.all(
        factionsToRepair.map((faction) =>
          repairFactionEmptyLineupSnapshots(faction.id, { askConfirm: false })
        )
      );
      const fixedCount = results.filter((result) => result.fixed).length;
      const removedCount = results.reduce(
        (sum, result) => sum + (result.removedCount || 0),
        0
      );

      await loadData();
      setMessage(`Correzione line-up completata con successo: ${fixedCount} fazion${fixedCount === 1 ? 'e corretta' : 'i corrette'}, ${removedCount} snapshot vuot${removedCount === 1 ? 'a rimossa' : 'e rimosse'}.`);
    } catch (err) {
      console.error(err);
      setMessage('Errore durante la correzione line-up globale');
    } finally {
      setIsRepairingLineups(false);
    }
  };


  // Crea una funzione per filtrare le fazioni Indy
  const indyFactions = useMemo(() => {
    return filteredAndSortedFactions.filter(f => getFactionRosterLabel(f, rosterMap) === 'Indy');
  }, [filteredAndSortedFactions, rosterMap]);

  // Funzione per spostare tutte le fazioni Indy
  const handleBulkMove = async () => {
    if (!bulkMoveTarget || !window.confirm(`Spostare TUTTE le fazioni Indy nel roster "${bulkMoveTarget}"?`)) return;

    try {
      const batch = [];
      const rostersCollection = collection(db, 'factions');

      // Prepara tutte le operazioni di update
      indyFactions.forEach(f => {
        const docRef = doc(rostersCollection, f.id);
        batch.push(updateDoc(docRef, {
          roster: bulkMoveTarget,
          updatedAt: serverTimestamp()
        }));
      });

      // Esegui tutte le operazioni in batch
      await Promise.all(batch);
      await loadData(); // Ricarica i dati
      setMessage(`Tutte le fazioni Indy sono state spostate in ${bulkMoveTarget}`);
      setBulkMoveTarget('');
    } catch (err) {
      console.error(err);
      setMessage('Errore durante lo spostamento');
    }
  };

  // Animazione per overlay modale
  const modalOverlayStyle = {
    background: 'rgba(0,0,0,0.45)',
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1050,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.3s',
    animation: 'fadeIn 0.3s',
  };
  const modalContentStyle = {
    background: '#fff',
    borderRadius: '18px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    minWidth: 320,
    maxWidth: 600,
    width: '100%',
    animation: 'slideDown 0.35s',
    padding: 0,
  };
  // Animazioni keyframes
  const styleAnim = `
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideDown { from { transform: translateY(-40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    .btn-animated { transition: background 0.2s, color 0.2s, box-shadow 0.2s; border-radius: 8px; }
    .btn-animated:hover, .btn-animated:focus { box-shadow: 0 2px 12px rgba(0,0,0,0.10); background: #0d6efd; color: #fff; }
    .input-animated { transition: box-shadow 0.2s, border 0.2s; border-radius: 8px; }
    .input-animated:focus { box-shadow: 0 0 0 2px #0d6efd33; border-color: #0d6efd; }
  `;
  const isPositiveMessage =
    message.includes('successo') ||
    message.includes('Nessuna') ||
    message.includes('annullata');
  const formatLineupDate = (date) =>
    date ? date.toLocaleString('it-IT') : '-';
  const renderLineupWrestlerBadges = (wrestlerIds, badgeClassName) => {
    const uniqueIds = [...new Set((wrestlerIds || []).filter(Boolean))];

    if (uniqueIds.length === 0) {
      return <span className="text-muted">-</span>;
    }

    return (
      <div className="d-flex flex-wrap gap-1">
        {uniqueIds.map((wrestlerId) => (
          <span key={wrestlerId} className={`badge ${badgeClassName}`}>
            {getWrestlerDisplayName(wrestlerId, editingFactionWrestlerNameMap)}
          </span>
        ))}
      </div>
    );
  };

  return (
    <ProtectedRoute>
      <style>{styleAnim}</style>
      <div className="container admin-page py-4">
        <div className="admin-page-heading">
          <div>
            <h1 className="fw-bold">Gestione Fazioni</h1>
          </div>
        </div>

        <div className="admin-panel mb-4">
          <div className="d-flex align-items-center justify-content-end gap-3 flex-wrap">
            <div className="d-flex align-items-center gap-2">
              <button
                className={`btn btn-${swapsEnabled ? 'success' : 'secondary'} btn-animated`}
                onClick={handleToggleSwaps}
                disabled={swapsLoading}
              >
                <i className={`bi ${swapsEnabled ? 'bi-unlock' : 'bi-lock'} me-2`}></i>
                {swapsEnabled ? 'Scambi abilitati' : 'Scambi bloccati'}
              </button>
              {swapsBlockedAt && (
                <span className="text-muted small">
                  Bloccati il {swapsBlockedAt.toLocaleString('it-IT', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {swapsError && <span className="text-danger ms-2">{swapsError}</span>}
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                className={`btn btn-${swapsRoadToEnabled ? 'success' : 'secondary'} btn-animated`}
                onClick={handleToggleSwapsRoadTo}
                disabled={swapsRoadToLoading}
              >
                <i className={`bi ${swapsRoadToEnabled ? 'bi-unlock' : 'bi-lock'} me-2`}></i>
                {swapsRoadToEnabled ? 'Road To: Scambi abilitati' : 'Road To: Scambi bloccati'}
              </button>
              {swapsRoadToBlockedAt && (
                <span className="text-muted small">
                  Bloccati il {swapsRoadToBlockedAt.toLocaleString('it-IT', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {swapsRoadToError && <span className="text-danger ms-2">{swapsRoadToError}</span>}
            </div>
          </div>
        </div>
        {/* Form Modifica - visibile solo quando si sta modificando una fazione */}
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-primary text-white py-3 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 d-flex align-items-center">
              <i className="bi bi-pencil-square me-2"></i>
              Modifica Fazione
            </h5>
          </div>
          <div className="card-body">
            {editingId && (
              <>
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    {/* ...existing code... */}
                    {/* Sezione Informazioni Base */}
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Nome Fazione*</label>
                        <input
                          className="form-control input-animated"
                          value={form.name}
                          onChange={e => updateField('name', e.target.value)}
                          placeholder="Inserisci nome fazione"
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-bold">Giocatore*</label>
                        <select
                          className="form-select input-animated"
                          value={form.ownerUid}
                          onChange={e => updateField('ownerUid', e.target.value)}
                          required
                        >
                          <option value="">Seleziona giocatore...</option>
                          {users.map(u =>
                            <option key={u.id} value={u.id}>
                              {u.displayName || u.username || u.email}
                            </option>
                          )}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Tipo Gioco</label>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className={`btn ${form.gameType === 'Competitivo' ? 'btn-primary' : 'btn-outline-primary'} flex-grow-1`}
                            onClick={() => updateField('gameType', 'Competitivo')}
                          >
                            <i className="bi bi-trophy me-2"></i> Competitivo
                          </button>
                          <button
                            type="button"
                            className={`btn ${form.gameType === 'Indipendente' ? 'btn-warning' : 'btn-outline-warning'} flex-grow-1`}
                            onClick={() => updateField('gameType', 'Indipendente')}
                          >
                            <i className="bi bi-emoji-sunglasses me-2"></i> Indipendente
                          </button>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-bold">Roster</label>
                        <select
                          className="form-select input-animated"
                          value={form.roster}
                          onChange={e => updateField('roster', e.target.value)}
                        >
                          <option value="">Seleziona roster esistente...</option>
                          {rosters.map(r => {
                            const start = formatRosterStartDate(r.dataInizioPunteggio);
                            return (
                              <option key={r.id} value={r.name}>
                                {r.name}{start ? ` — ${start}` : ''}
                              </option>
                            );
                          })}
                        </select>
                        <div className="form-text text-muted">Per creare un nuovo roster vai nella sezione Gestione Roster dell'admin.</div>
                      </div>
                    </div>

                    {/* Sezione Wrestlers o banner scambi disattivati */}
                    <div className="col-12">
                      {form.gameType === 'Indipendente' && !swapsEnabled ? (
                        renderSwapBanner()
                      ) : (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">
                              <i className="bi bi-people-fill me-2"></i>
                              Wrestlers e Costi
                            </h5>
                            <button
                              type="button"
                              className="btn btn-sm btn-success btn-animated"
                              onClick={addRow}
                            >
                              <i className="bi bi-plus-lg me-1"></i> Aggiungi
                            </button>
                          </div>
                          <div className="table-responsive">
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th style={{ width: '60%' }}>Wrestler</th>
                                  <th style={{ width: '30%' }}>Costo</th>
                                  <th style={{ width: '10%' }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {form.costs.map((m, i) => (
                                  <tr key={i}>
                                    <td>
                                      <select
                                        className="form-select form-select-sm input-animated"
                                        value={m.wrestlerId}
                                        onChange={e => updateCost(i, 'wrestlerId', e.target.value)}
                                        required
                                      >
                                        <option value="">Seleziona wrestler...</option>
                                        {wrestlers.map(w =>
                                          <option key={w.id} value={w.id}>{w.name}</option>
                                        )}
                                      </select>
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        className="form-control form-control-sm input-animated"
                                        min="0"
                                        value={m.cost}
                                        onChange={e => updateCost(i, 'cost', e.target.value)}
                                        required
                                      />
                                    </td>
                                    <td className="text-center">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger btn-animated"
                                        onClick={() => removeRow(i)}
                                        disabled={form.costs.length === 1}
                                        title="Rimuovi"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {/* Riepilogo crediti con progress bar */}
                          <div className={`alert ${creditsLeft < 0 ? 'alert-danger' : 'alert-success'} mt-3`}>
                            <div className="d-flex justify-content-between mb-2">
                              <strong>Crediti disponibili: {creditsLeft}</strong>
                              <small>Totale costi: {totalCost}</small>
                            </div>
                            <div className="progress" style={{ height: '10px' }}>
                              <div
                                className={`progress-bar ${creditsLeft < 0 ? 'bg-danger' : 'bg-success'}`}
                                role="progressbar"
                                style={{ width: `${Math.min(100, (totalCost / (totalCost + Math.max(0, creditsLeft))) * 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Storico Line-Up - Solo in modifica */}
                    {editingId && (
                      <div className="col-12">
                        <div className="card border-warning">
                          <div className="card-header bg-warning bg-opacity-10 py-2">
                            <h6 className="mb-0">
                              <i className="bi bi-clock-history me-2"></i>
                              Storico Line-Up
                            </h6>
                          </div>
                          <div className="card-body p-3">
                            {editingLineupHistoryRows.length > 0 ? (
                              <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                  <thead>
                                    <tr>
                                      <th style={{ minWidth: '150px' }}>Da</th>
                                      <th style={{ minWidth: '120px' }}>Stato</th>
                                      <th style={{ minWidth: '170px' }}>Main Eventer x2</th>
                                      <th style={{ minWidth: '170px' }}>Backstage x0</th>
                                      <th style={{ minWidth: '220px' }}>In-Ring x1</th>
                                      <th style={{ minWidth: '150px' }}>Scadenza ME</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {editingLineupHistoryRows.map((row) => {
                                      const assignedDate = row.mainEventerAssignedAt;
                                      const effectiveDate = row.effectiveFrom || assignedDate;
                                      const hasDifferentAssignedDate =
                                        assignedDate &&
                                        row.effectiveFrom &&
                                        assignedDate.getTime() !== row.effectiveFrom.getTime();

                                      return (
                                        <tr key={row.key} className={row.isEmpty ? 'table-warning' : ''}>
                                          <td>
                                            <span className="fw-semibold">
                                              {formatLineupDate(effectiveDate)}
                                            </span>
                                            {hasDifferentAssignedDate && (
                                              <small className="text-muted d-block">
                                                ME assegnato: {formatLineupDate(assignedDate)}
                                              </small>
                                            )}
                                          </td>
                                          <td>
                                            <div className="d-flex flex-wrap gap-1">
                                              {row.isCurrent && (
                                                <span className="badge bg-primary">Attuale</span>
                                              )}
                                              {row.source === 'history' && (
                                                <span className="badge bg-secondary">Storico</span>
                                              )}
                                              {row.source === 'legacy' && (
                                                <span className="badge bg-dark">Legacy</span>
                                              )}
                                              {row.isEmpty && (
                                                <span className="badge bg-warning text-dark">Vuota</span>
                                              )}
                                            </div>
                                          </td>
                                          <td>
                                            {row.champion ? (
                                              <span className="badge bg-warning text-dark">
                                                {getWrestlerDisplayName(row.champion, editingFactionWrestlerNameMap)}
                                              </span>
                                            ) : (
                                              <span className="text-muted">-</span>
                                            )}
                                          </td>
                                          <td>
                                            {renderLineupWrestlerBadges(row.backstage, 'bg-secondary')}
                                          </td>
                                          <td>
                                            {renderLineupWrestlerBadges(row.inRing, 'bg-success')}
                                          </td>
                                          <td>
                                            <span className="text-muted">
                                              {formatLineupDate(row.mainEventerExpiresAt)}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-muted small">
                                Nessuno storico line-up salvato.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Swap Wrestler - Solo in modifica */}
                    {editingId && (
                      <div className="col-12">
                        <div className="card border-primary">
                          <div className="card-header bg-primary bg-opacity-10 py-2">
                            <h6 className="mb-0">
                              <i className="bi bi-arrow-left-right me-2"></i>
                              Swap Wrestler
                            </h6>
                          </div>
                          <div className="card-body p-3">
                            <SwapWrestler factionId={editingId} onSwapped={loadData} />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Pulsanti di azione */}
                    <div className="col-12 mt-3">
                      <div className="d-flex gap-2 justify-content-end mt-3">
                        {editingId && (
                          <button type="button" className="btn btn-outline-secondary btn-animated" onClick={resetForm}>
                            <i className="bi bi-x-lg me-1"></i> Annulla
                          </button>
                        )}
                        <button type="submit" className="btn btn-primary btn-animated" disabled={creditsLeft < 0}>
                          <i className="bi bi-check-lg me-1"></i>
                          Aggiorna
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
                {/* Messaggio di stato */}
                {message && (
                  <div className={`alert ${isPositiveMessage ? 'alert-success' : 'alert-danger'} mt-3 d-flex align-items-center`}>
                    <i className={`bi ${isPositiveMessage ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                    {message}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Lista Fazioni - mostra sempre sotto il form */}
        <div className="card shadow-sm admin-list-card">
          <div className="card-header bg-light py-3">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
              <h5 className="mb-0">
                <i className="bi bi-list-ul me-2"></i>
                Lista Fazioni ({filteredAndSortedFactions.length})
              </h5>

              <div className="d-flex flex-column flex-md-row gap-2 w-100 w-md-auto">
                {/* Filtri in linea */}
                <div className="input-group flex-grow-1">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Cerca fazione, roster..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="d-flex gap-2">
                  <select
                    className="form-select flex-grow-1"
                    value={gameTypeFilter}
                    onChange={(e) => setGameTypeFilter(e.target.value)}
                  >
                    <option value="all">Tutti i tipi</option>
                    <option value="Competitivo">Competitivo</option>
                    <option value="Indipendente">Indipendente</option>
                  </select>

                  <select
                    className="form-select flex-grow-1"
                    value={rosterFilter}
                    onChange={(e) => setRosterFilter(e.target.value)}
                  >
                    <option value="all">Tutti i roster</option>
                    {[...new Set(rosters
                      .filter(r => r && r.name) // Guard against undefined rosters
                      .sort((a, b) => {
                        const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
                        const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
                        return dateB - dateA;
                      })
                      .map(r => ({ name: r.name, start: formatRosterStartDate(r.dataInizioPunteggio) })))]
                      .map(({ name, start }) => (
                        <option key={name} value={name}>
                          {name}{start ? ` — ${start}` : ''}
                        </option>
                      ))
                    }
                  </select>
                  <button
                    className={`btn ${emptyLineupRepairStats.snapshotCount > 0 ? 'btn-outline-warning' : 'btn-outline-success'} btn-animated d-flex align-items-center text-nowrap`}
                    onClick={handleRepairAllLineupHistories}
                    disabled={isRepairingLineups}
                    title={
                      emptyLineupRepairStats.snapshotCount > 0
                        ? `Correggi ${emptyLineupRepairStats.snapshotCount} snapshot line-up vuote in ${emptyLineupRepairStats.factionCount} fazioni`
                        : 'Verifica line-up di tutte le fazioni'
                    }
                  >
                    {isRepairingLineups ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Correzione...
                      </>
                    ) : (
                      <>
                        <i className={`bi ${emptyLineupRepairStats.snapshotCount > 0 ? 'bi-wrench-adjustable' : 'bi-check-circle'} me-1`}></i>
                        {emptyLineupRepairStats.snapshotCount > 0
                          ? `Correggi line-up (${emptyLineupRepairStats.snapshotCount})`
                          : 'Verifica line-up'}
                      </>
                    )}
                  </button>
                  {/* Pulsante azzera punti per tutto il roster selezionato */}
                  {rosterFilter !== 'all' && (
                    <button
                      className="btn btn-warning btn-animated d-flex align-items-center"
                      onClick={handleResetAllPointsInRoster}
                      disabled={isResetting}
                      title={`Azzera punti per tutto il roster ${rosterFilter}`}
                    >
                      {isResetting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Reset...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-counterclockwise me-1"></i>
                          Azzera punti
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabella più compatta */}
          <div className="table-responsive">
            <table className="table table-hover table-striped mb-0">
              <thead className="table-light">
                <tr>
                  <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                    <div className="d-flex align-items-center">
                      Nome
                      {sortField === 'name' && (
                        <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ms-1`}></i>
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('roster')} style={{ cursor: 'pointer' }}>
                    <div className="d-flex align-items-center">
                      Roster
                      {sortField === 'roster' && (
                        <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ms-1`}></i>
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('numWrestler')} style={{ cursor: 'pointer' }}>N Wrestler</th>
                  <th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>Tipo</th>
                  {/* AGGIUNTA: Colonna Data Creazione */}
                  <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                    <div className="d-flex align-items-center">
                      Data Creazione
                      {sortField === 'date' && (
                        <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ms-1`}></i>
                      )}
                    </div>
                  </th>
                  <th style={{ width: '110px' }}>Line-up</th>
                  <th style={{ width: '170px' }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedFactions.length > 0 ? (
                  filteredAndSortedFactions.map(f => {
                    const owner = users.find(u => u.id === f.ownerUid);
                    const rosterLabel = getFactionRosterLabel(f, rosterMap) || '-';
                    const wrestlerCount = getFactionWrestlerCount(f);
                    const factionGameType = getFactionGameType(f, rosterMap) || '-';
                    const createdAt = normalizeDateValue(f.createdAt);
                    const emptyLineupCount = getEmptyLineupSnapshotCount(f);
                    return (
                      <tr key={f.id}>
                        <td>
                          <div className="fw-bold text-light">{f.name}</div>
                          <small className="text-muted d-block">
                            {owner?.displayName || owner?.username || 'Sconosciuto'}
                          </small>
                        </td>
                        <td className='text-light'>
                          {rosterLabel}
                        </td>
                        <td className={`fw-bold ${wrestlerCount > 0 ? 'text-success' : 'text-danger'}`}>
                          {wrestlerCount}
                        </td>
                        <td>
                          <span className={`badge ${factionGameType === 'Competitivo' ? 'bg-brand-secondary text-brand-primary' : 'bg-brand-primary'}`}>
                            {factionGameType}
                          </span>
                        </td>
                        <td>
                          <span className="text-brand-muted" style={{ whiteSpace: 'nowrap' }}>
                            {createdAt ? createdAt.toLocaleString('it-IT') : '-'}
                          </span>
                        </td>
                        <td>
                          {emptyLineupCount > 0 ? (
                            <span className="badge bg-warning text-dark">
                              {emptyLineupCount} vuot{emptyLineupCount === 1 ? 'a' : 'e'}
                            </span>
                          ) : (
                            <span className="badge bg-success">OK</span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-outline-primary btn-animated"
                              onClick={() => startEdit(f)}
                              title="Modifica"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary btn-animated"
                              onClick={() => handleResetPoints(f.id)}
                              title="Azzera punti"
                            >
                              <i className="bi bi-arrow-counterclockwise"></i>
                            </button>
                            <button
                              className={`btn btn-sm ${emptyLineupCount > 0 ? 'btn-outline-warning' : 'btn-outline-success'} btn-animated`}
                              onClick={() => handleRepairFactionLineupHistory(f.id)}
                              disabled={emptyLineupCount === 0 || repairingFactionId === f.id || isRepairingLineups}
                              title={emptyLineupCount > 0 ? 'Correggi snapshot line-up vuote' : 'Line-up OK'}
                            >
                              {repairingFactionId === f.id ? (
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                              ) : (
                                <i className="bi bi-wrench-adjustable"></i>
                              )}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger btn-animated"
                              onClick={() => handleDelete(f.id)}
                              title="Elimina"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-5"> {/* CORRETTO: 7 colonne */}
                      <div className="text-muted">
                        <i className="bi bi-emoji-frown fs-1"></i>
                        <p className="mt-3">Nessuna fazione trovata</p>
                        {searchTerm && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setSearchTerm('');
                              setGameTypeFilter('all');
                              setRosterFilter('all');
                            }}
                          >
                            Resetta filtri
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal per creazione nuovo roster */}
      {/* Modale creazione roster rimossa: ora la creazione avviene solo da Gestione Roster */}

    </ProtectedRoute>
  );
}
